import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Wand2 } from 'lucide-react';

import { cn } from '@/lib/utils';

import { loadFont } from '../fonts';
import { makeText } from '../objects';
import type { BrandInfo } from '../types';
import type { DesignEditorApi } from '../useDesignEditor';
import { SectionLabel } from '../ui';

/**
 * The vendor's brand, one click away: their marks, their colours (click to
 * paint the selection, or the artboard when nothing is selected), their
 * typeface, and "Apply brand" — recolour and retype a whole design by the roles
 * its layers play.
 */
export const BrandPanel: React.FC<{ ed: DesignEditorApi; brand: BrandInfo; hasKit: boolean }> = ({ ed, brand, hasKit }) => {
    const { t } = useTranslation();
    const [applying, setApplying] = useState(false);
    useEffect(() => { if (brand.font) void loadFont(brand.font, 400); }, [brand.font]);
    const logos = [brand.logoUrl, brand.altLogoUrl].filter(Boolean) as string[];
    const roleSwatches: Array<{ key: string; color: string }> = [
        { key: 'INK', color: brand.ink },
        { key: 'GROUND', color: brand.ground },
        { key: 'ACCENT', color: brand.accent },
    ];
    const extra = brand.colors.filter((c) => ![brand.ink, brand.ground, brand.accent].map((x) => x.toLowerCase()).includes(c.toLowerCase()));

    const paint = (color: string) => {
        if (ed.selection.length) ed.setColor(color);
        else ed.setArtboardColor(color);
    };

    const addBrandHeading = async () => {
        if (!brand.font) return;
        await loadFont(brand.font, 400);
        const a = ed.artboard;
        const u = Math.min(a.w, a.h);
        const tb = makeText(brand.name || t('DESIGN_EDITOR.TPL_BRAND'), {
            x: a.w * 0.14, y: a.h / 2 - u * 0.05, w: a.w * 0.72, size: u * 0.08, font: brand.font,
            color: brand.ink, align: 'center', lineHeight: 1.05, fontRole: 'heading', colorRole: 'ink', name: 'Brand heading',
        });
        ed.addObjects([tb]);
    };

    return (
        <div className="flex flex-col gap-5">
            {!hasKit && (
                <div className="rounded-xl border border-dashed p-3 text-xs leading-relaxed text-muted-foreground">
                    {t('DESIGN_EDITOR.BRAND_EMPTY')}
                </div>
            )}

            <button
                type="button"
                onClick={async () => { setApplying(true); try { await ed.applyBrand(); } finally { setApplying(false); } }}
                disabled={applying}
                className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-foreground bg-background text-sm font-medium text-foreground transition-colors hover:bg-foreground/[0.04] disabled:opacity-60 [&_svg]:size-4"
            >
                {applying ? <Loader2 className="animate-spin" /> : <Wand2 />}
                {t('DESIGN_EDITOR.APPLY_BRAND')}
            </button>
            <p className="-mt-3 text-[11px] leading-snug text-muted-foreground">{t('DESIGN_EDITOR.APPLY_BRAND_HINT')}</p>

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.BRAND_LOGOS')}</SectionLabel>
                {logos.length === 0 && <div className="text-xs text-muted-foreground">{t('DESIGN_EDITOR.BRAND_NO_LOGO')}</div>}
                <div className="grid grid-cols-2 gap-2">
                    {logos.map((url) => (
                        <button
                            key={url}
                            type="button"
                            onClick={() => { void ed.addLogo(url); }}
                            title={t('DESIGN_EDITOR.ADD_LOGO')}
                            // A light checkerboard reads both light and dark marks.
                            className="flex h-20 cursor-pointer items-center justify-center rounded-xl border bg-[repeating-conic-gradient(#f3f1ee_0%_25%,#ffffff_0%_50%)] bg-[length:12px_12px] p-3 transition-colors hover:border-foreground/40"
                        >
                            <img src={url} alt="" className="max-h-full max-w-full object-contain" />
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.BRAND_COLORS')}</SectionLabel>
                <div className="grid grid-cols-3 gap-2">
                    {roleSwatches.map((s) => (
                        <button
                            key={s.key}
                            type="button"
                            onClick={() => paint(s.color)}
                            className="flex cursor-pointer flex-col gap-1.5 text-start"
                        >
                            <span className="h-12 w-full rounded-lg ring-1 ring-foreground/10 transition-transform hover:scale-[1.03]" style={{ backgroundColor: s.color }} />
                            <span className="text-[11px] text-foreground">{t(`DESIGN_EDITOR.ROLE_${s.key}`)}</span>
                            <span className="-mt-1 text-[10.5px] tabular-nums text-muted-foreground uppercase">{s.color}</span>
                        </button>
                    ))}
                </div>
                {extra.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {extra.map((c) => (
                            <button
                                key={c}
                                type="button"
                                title={c}
                                onClick={() => paint(c)}
                                className={cn('size-7 cursor-pointer rounded-md ring-1 ring-foreground/10 transition-transform hover:scale-110')}
                                style={{ backgroundColor: c }}
                            />
                        ))}
                    </div>
                )}
                <p className="text-[11px] leading-snug text-muted-foreground">{t('DESIGN_EDITOR.BRAND_COLORS_HINT')}</p>
            </div>

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.BRAND_FONT')}</SectionLabel>
                {brand.font ? (
                    <button
                        type="button"
                        onClick={() => { void addBrandHeading(); }}
                        className="flex h-16 cursor-pointer items-center justify-center rounded-xl border bg-background px-3 text-[22px] text-foreground transition-colors hover:border-foreground/40"
                        style={{ fontFamily: `"${brand.font}", serif` }}
                    >
                        {brand.font}
                    </button>
                ) : (
                    <div className="text-xs text-muted-foreground">{t('DESIGN_EDITOR.BRAND_NO_FONT')}</div>
                )}
            </div>
        </div>
    );
};
